import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Globe, Filter, ListChecks, Ban, Check, Plus, Minus, Eye, User, Loader2 } from 'lucide-react';
import WebsiteCategoryGroups from '@/components/WebsiteCategoryGroups';
import { useEffectiveSerial } from '@/hooks/useEffectiveSerial';
import { useToast } from '@/hooks/use-toast';
import { useMentorStatus } from '@/hooks/useMentorStatus';
import { useDeviceMutations } from '@/hooks/useDeviceMutations';
import { useDeviceBrowserMode } from '@/hooks/useDeviceBrowserMode';
import { useSiteCategories } from '@/hooks/useSiteCategories';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

const browserModeOptions = [
  {
    id: 'filtered-browser',
    title: 'Filtered Browser',
    description: 'Full browsing with content filtering. Sites are organized by category with blocked and allowed lists. You control which categories are accessible, plus maintain custom whitelist and blacklist.',
    icon: Filter,
    gradient: 'from-primary to-accent',
  },
  {
    id: 'whitelisted-browser',
    title: 'Whitelisted Browser',
    description: 'Maximum control over web access. Only websites you specifically approve can be visited. Best for younger users or when you want complete oversight of browsing activity.',
    icon: ListChecks,
    gradient: 'from-primary to-primary-hover',
  },
  {
    id: 'no-browser',
    title: 'No Browser',
    description: 'Browser access is completely disabled. The user cannot browse any websites. Best when web access is not needed or for very young users.',
    icon: Ban,
    gradient: 'from-destructive to-destructive/80',
  },
];


// Content filtering levels
const contentFilterLevels = [
  {
    id: 'light',
    title: 'Light',
    description: 'Blocks explicit adult content, pornography, and overtly suggestive material.',
    examples: ['Pornography', 'Explicit nudity', 'Graphic sexual content'],
  },
  {
    id: 'medium',
    title: 'Medium',
    description: 'Blocks all Light content plus additional inappropriate material including provocative imagery and mature themes.',
    examples: ['All Light filters', 'Provocative poses', 'Mature themes'],
  },
  {
    id: 'strict',
    title: 'Strict',
    description: 'Maximum filtering. Blocks all Medium content plus any revealing clothing, swimwear, and borderline content.',
    examples: ['All Medium filters', 'Bikinis & swimwear', 'Revealing clothing', 'Any suggestive content'],
  },
];

type RequestAction = {
  type: 'category' | 'whitelist' | 'blacklist';
  action: 'block' | 'allow' | 'add' | 'remove';
  name: string;
};

const Browser: React.FC = () => {
  const navigate = useNavigate();
  const { serial } = useEffectiveSerial();
  const { toast } = useToast();
  const { canChangeDirectly } = useMentorStatus();
  const { updateBrowserConfig } = useDeviceMutations();
  const { currentMode: currentModeId } = useDeviceBrowserMode();
  const { categories, allowlist, blocklist, config, blueJayRules, isLoading } = useSiteCategories();
  
  const [requestAction, setRequestAction] = useState<RequestAction | null>(null);
  const [showItemConfirmation, setShowItemConfirmation] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Content restriction state - initialized from real config
  const [selectedFilterLevel, setSelectedFilterLevel] = useState<string>(config.nsfwLevel);
  const [blockHumansEnabled, setBlockHumansEnabled] = useState(config.humanBlur);
  
  // Content restriction confirmation state
  const [pendingFilterLevel, setPendingFilterLevel] = useState<string | null>(null);
  const [showFilterConfirmation, setShowFilterConfirmation] = useState(false);
  const [pendingHumanBlock, setPendingHumanBlock] = useState<boolean | null>(null);
  const [showHumanBlockConfirmation, setShowHumanBlockConfirmation] = useState(false);
  
  // Site input state
  const [newWhitelistSite, setNewWhitelistSite] = useState('');
  const [newBlacklistSite, setNewBlacklistSite] = useState('');

  // Sync state with config when it loads
  useEffect(() => {
    if (!isLoading) {
      setSelectedFilterLevel(config.nsfwLevel);
      setBlockHumansEnabled(config.humanBlur);
    }
  }, [config.nsfwLevel, config.humanBlur, isLoading]);

  const currentOption = browserModeOptions.find((opt) => opt.id === currentModeId) || browserModeOptions[0];
  const CurrentIcon = currentOption.icon;

  const handleItemRequest = (type: RequestAction['type'], action: RequestAction['action'], name: string) => {
    setRequestAction({ type, action, name });
    setShowItemConfirmation(true);
  };

  const handleConfirmItemRequest = async () => {
    if (requestAction) {
      setIsSaving(true);
      try {
        let message = '';
        if (canChangeDirectly) {
          // Actually call the mutation
          if (requestAction.type === 'category') {
            // Get current blocked categories array from blueJayRules
            const currentBlockedCategories: string[] = 
              blueJayRules && typeof blueJayRules === 'object' && Array.isArray(blueJayRules.categories)
                ? blueJayRules.categories
                : [];
            
            let newCategories: string[];
            
            if (requestAction.action === 'block') {
              // Block = ADD to categories array (if not already there)
              if (!currentBlockedCategories.includes(requestAction.name)) {
                newCategories = [...currentBlockedCategories, requestAction.name];
              } else {
                newCategories = currentBlockedCategories;
              }
            } else {
              // Allow = REMOVE from categories array
              newCategories = currentBlockedCategories.filter(cat => cat !== requestAction.name);
            }
            
            // Call mutation to update categories
            await updateBrowserConfig({ categories: newCategories });
            message = `"${requestAction.name}" has been ${requestAction.action === 'block' ? 'blocked' : 'allowed'}.`;
          } else if (requestAction.action === 'add') {
            // Helper to get site names from either array or object format
            const getSiteNames = (data: any): string[] => {
              if (!data) return [];
              if (Array.isArray(data)) return data;
              if (typeof data === 'object') return Object.keys(data);
              return [];
            };
            // Get current lists as arrays of site names
            const currentAllowlist: string[] = 
              blueJayRules && typeof blueJayRules === 'object'
                ? getSiteNames(blueJayRules.allowlist)
                : [];
            const currentBlocklist: string[] = 
              blueJayRules && typeof blueJayRules === 'object'
                ? getSiteNames(blueJayRules.blocklist)
                : [];
            
            const siteName = requestAction.name.toLowerCase();
            
            if (requestAction.type === 'whitelist') {
              // Add to allowlist array, convert to object for mutation
              const newAllowlist = [...currentAllowlist, siteName];
              await updateBrowserConfig({ allowlist: newAllowlist.reduce((acc, site) => ({ ...acc, [site]: true }), {} as Record<string, boolean>) });
            } else {
              // Add to blocklist array, convert to object for mutation
              const newBlocklist = [...currentBlocklist, siteName];
              await updateBrowserConfig({ blocklist: newBlocklist.reduce((acc, site) => ({ ...acc, [site]: true }), {} as Record<string, boolean>) });
            }
            message = `"${siteName}" has been added to the ${requestAction.type}.`;
          } else {
            // Remove from list - use same helper
            const getSiteNames = (data: any): string[] => {
              if (!data) return [];
              if (Array.isArray(data)) return data;
              if (typeof data === 'object') return Object.keys(data);
              return [];
            };
            const currentAllowlist: string[] = 
              blueJayRules && typeof blueJayRules === 'object'
                ? getSiteNames(blueJayRules.allowlist)
                : [];
            const currentBlocklist: string[] = 
              blueJayRules && typeof blueJayRules === 'object'
                ? getSiteNames(blueJayRules.blocklist)
                : [];
            
            if (requestAction.type === 'whitelist') {
              const newAllowlist = currentAllowlist.filter((site: string) => site !== requestAction.name);
              await updateBrowserConfig({ allowlist: newAllowlist.reduce((acc, site) => ({ ...acc, [site]: true }), {} as Record<string, boolean>) });
            } else {
              const newBlocklist = currentBlocklist.filter((site: string) => site !== requestAction.name);
              await updateBrowserConfig({ blocklist: newBlocklist.reduce((acc, site) => ({ ...acc, [site]: true }), {} as Record<string, boolean>) });
            }
            message = `"${requestAction.name}" has been removed from the ${requestAction.type}.`;
          }
          toast({
            title: "Settings Updated",
            description: message,
          });
        } else {
          // Submit request
          if (requestAction.type === 'category') {
            message = `Your request to ${requestAction.action} "${requestAction.name}" has been submitted.`;
          } else if (requestAction.action === 'add') {
            message = `Your request to add "${requestAction.name}" to the ${requestAction.type === 'whitelist' ? 'whitelist' : 'blacklist'} has been submitted.`;
          } else {
            message = `Your request to remove "${requestAction.name}" from the ${requestAction.type === 'whitelist' ? 'whitelist' : 'blacklist'} has been submitted.`;
          }
          toast({
            title: "Request Submitted",
            description: message,
          });
        }
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to update setting. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsSaving(false);
      }
    }
    setShowItemConfirmation(false);
    setRequestAction(null);
  };

  const handleAddWhitelistSite = () => {
    if (newWhitelistSite.trim()) {
      handleItemRequest('whitelist', 'add', newWhitelistSite.trim());
      setNewWhitelistSite('');
    }
  };

  const handleAddBlacklistSite = () => {
    if (newBlacklistSite.trim()) {
      handleItemRequest('blacklist', 'add', newBlacklistSite.trim());
      setNewBlacklistSite('');
    }
  };

  const handleFilterLevelClick = (level: string) => {
    if (level === selectedFilterLevel) return;
    setPendingFilterLevel(level);
    setShowFilterConfirmation(true);
  };

  const handleConfirmFilterChange = async () => {
    if (pendingFilterLevel) {
      if (canChangeDirectly) {
        setIsSaving(true);
        try {
          await updateBrowserConfig({ nsfwLevel: pendingFilterLevel });
          setSelectedFilterLevel(pendingFilterLevel);
          toast({
            title: "Settings Updated",
            description: `Content filtering changed to "${pendingFilterLevel}".`,
          });
        } catch (error) {
          toast({
            title: "Error",
            description: "Failed to update setting. Please try again.",
            variant: "destructive",
          });
        } finally {
          setIsSaving(false);
        }
      } else {
        toast({
          title: "Request Submitted",
          description: `Your request to change content filtering to "${pendingFilterLevel}" has been submitted.`,
        });
      }
    }
    setShowFilterConfirmation(false);
    setPendingFilterLevel(null);
  };

  const handleHumanBlockClick = (enabled: boolean) => {
    if (enabled === blockHumansEnabled) return;
    setPendingHumanBlock(enabled);
    setShowHumanBlockConfirmation(true);
  };

  const handleConfirmHumanBlockChange = async () => {
    if (pendingHumanBlock !== null) {
      if (canChangeDirectly) {
        setIsSaving(true);
        try {
          await updateBrowserConfig({ humanBlur: pendingHumanBlock });
          setBlockHumansEnabled(pendingHumanBlock);
          toast({
            title: "Settings Updated",
            description: `Human detection blocking has been ${pendingHumanBlock ? 'enabled' : 'disabled'}.`,
          });
        } catch (error) {
          toast({
            title: "Error",
            description: "Failed to update setting. Please try again.",
            variant: "destructive",
          });
        } finally {
          setIsSaving(false);
        }
      } else {
        toast({
          title: "Request Submitted",
          description: `Your request to ${pendingHumanBlock ? 'enable' : 'disable'} human detection blocking has been submitted.`,
        });
      }
    }
    setShowHumanBlockConfirmation(false);
    setPendingHumanBlock(null);
  };

  const getPendingFilterLevelInfo = () => {
    return contentFilterLevels.find(l => l.id === pendingFilterLevel);
  };


  return (
    <div className="min-h-[100dvh] bg-gradient-to-b from-background to-muted/30 pb-28">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/70 backdrop-blur-2xl border-b border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.08)]">
        <div className="px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate(`/${serial}/settings`)}
            className="p-2 -ml-2 rounded-full hover:bg-muted/50 active:scale-95 transition-all"
          >
            <ChevronLeft className="w-6 h-6 text-foreground" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-foreground">Browser Settings</h1>
            <p className="text-[13px] text-muted-foreground">How web browsing is managed on this device</p>
          </div>
        </div>
      </div>

      <div className="px-4 pt-6 space-y-6">
        {/* Current Mode Info with Request Button */}
        <div className="bg-background/60 backdrop-blur-xl rounded-2xl border border-white/15 shadow-[0_4px_16px_rgba(0,0,0,0.08),inset_0_1px_1px_rgba(255,255,255,0.1)] p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 flex-1">
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${currentOption.gradient} flex items-center justify-center shadow-md flex-shrink-0`}>
                <CurrentIcon className="w-5 h-5 text-primary-foreground" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Currently Using</p>
                <p className="font-semibold text-[15px] text-foreground">{currentOption.title}</p>
              </div>
            </div>
            {canChangeDirectly && (
              <button
                onClick={() => navigate(`/${serial}/settings/browser/mode`)}
                className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-[12px] font-medium hover:bg-primary-hover active:scale-[0.97] transition-all flex-shrink-0"
              >
                Change
              </button>
            )}
          </div>
          <p className="text-[13px] text-muted-foreground leading-relaxed mt-3">
            {currentOption.description}
          </p>
        </div>

        {/* Dynamic Content Based on Current Mode */}
        {currentModeId === 'filtered-browser' && (
          <Tabs defaultValue="categories" className="w-full">
            <TabsList className="w-full grid grid-cols-3 bg-muted/50 rounded-xl p-1 h-auto">
              <TabsTrigger 
                value="categories" 
                className="rounded-lg py-2.5 text-[13px] font-medium data-[state=active]:bg-background data-[state=active]:shadow-sm"
              >
                Categories
              </TabsTrigger>
              <TabsTrigger 
                value="sites" 
                className="rounded-lg py-2.5 text-[13px] font-medium data-[state=active]:bg-background data-[state=active]:shadow-sm"
              >
                Sites
              </TabsTrigger>
              <TabsTrigger 
                value="content" 
                className="rounded-lg py-2.5 text-[13px] font-medium data-[state=active]:bg-background data-[state=active]:shadow-sm"
              >
                Content
              </TabsTrigger>
            </TabsList>

            <TabsContent value="categories" className="mt-4">
              <WebsiteCategoryGroups
                categories={categories}
                onCategoryToggle={(categoryName, currentlyAllowed) => {
                  handleItemRequest('category', currentlyAllowed ? 'block' : 'allow', categoryName);
                }}
                canChangeDirectly={canChangeDirectly}
              />
            </TabsContent>

            <TabsContent value="sites" className="mt-4 space-y-3">
              <p className="text-[13px] text-muted-foreground px-1">
                Override category settings for specific sites. Whitelisted sites are always allowed, blacklisted sites are always blocked.
              </p>

              <Accordion type="single" collapsible className="space-y-3">
                {/* Whitelisted Sites */}
                <AccordionItem value="whitelisted-sites" className="bg-background rounded-2xl border border-border/50 shadow-sm overflow-hidden">
                  <AccordionTrigger className="px-4 py-4 hover:no-underline [&[data-state=open]]:border-b [&[data-state=open]]:border-border/50">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                        <Plus className="w-5 h-5 text-primary" />
                      </div>
                      <div className="text-left">
                        <p className="font-semibold text-[15px] text-foreground">Whitelisted Sites</p>
                        <p className="text-[12px] text-muted-foreground">{allowlist.length} sites always allowed</p>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-0">
                    {canChangeDirectly && (
                      <div className="px-4 py-3 flex gap-2 border-b border-border/50">
                        <Input
                          placeholder="Add website (e.g. example.com)"
                          value={newWhitelistSite}
                          onChange={(e) => setNewWhitelistSite(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleAddWhitelistSite()}
                           className="flex-1 h-9 text-[16px]"
                           autoComplete="off"
                           autoCorrect="off"
                        />
                        <Button size="sm" variant="outline" onClick={handleAddWhitelistSite} className="h-9 w-9 p-0">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                    <div className="divide-y divide-border/50">
                      {allowlist.length === 0 ? (
                        <div className="px-4 py-6 text-center text-muted-foreground text-[13px]">
                          No whitelisted sites yet
                        </div>
                      ) : (
                        allowlist.map((site) => (
                          <div
                            key={site.name}
                            className="w-full px-4 py-3 flex items-center justify-between text-left"
                          >
                            <div className="flex items-center gap-3">
                              <Globe className="w-4 h-4 text-primary" />
                              <div>
                                <p className="text-[14px] font-medium text-foreground">{site.name}</p>
                                <p className="text-[11px] text-muted-foreground">{site.category}</p>
                              </div>
                            </div>
                            {canChangeDirectly && (
                              <Button size="sm" variant="ghost" onClick={() => handleItemRequest('whitelist', 'remove', site.name)} className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive">
                                <Minus className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Blacklisted Sites */}
                <AccordionItem value="blacklisted-sites" className="bg-background rounded-2xl border border-border/50 shadow-sm overflow-hidden">
                  <AccordionTrigger className="px-4 py-4 hover:no-underline [&[data-state=open]]:border-b [&[data-state=open]]:border-border/50">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-destructive/10 flex items-center justify-center">
                        <Minus className="w-5 h-5 text-destructive" />
                      </div>
                      <div className="text-left">
                        <p className="font-semibold text-[15px] text-foreground">Blacklisted Sites</p>
                        <p className="text-[12px] text-muted-foreground">{blocklist.length} sites always blocked</p>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-0">
                    {canChangeDirectly && (
                      <div className="px-4 py-3 flex gap-2 border-b border-border/50">
                        <Input
                          placeholder="Add website (e.g. example.com)"
                          value={newBlacklistSite}
                          onChange={(e) => setNewBlacklistSite(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleAddBlacklistSite()}
                           className="flex-1 h-9 text-[16px]"
                           autoComplete="off"
                           autoCorrect="off"
                        />
                        <Button size="sm" variant="outline" onClick={handleAddBlacklistSite} className="h-9 w-9 p-0">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                    <div className="divide-y divide-border/50">
                      {blocklist.length === 0 ? (
                        <div className="px-4 py-6 text-center text-muted-foreground text-[13px]">
                          No blacklisted sites yet
                        </div>
                      ) : (
                        blocklist.map((site) => (
                          <div
                            key={site.name}
                            className="w-full px-4 py-3 flex items-center justify-between text-left"
                          >
                            <div className="flex items-center gap-3">
                              <Globe className="w-4 h-4 text-destructive" />
                              <div>
                                <p className="text-[14px] font-medium text-foreground">{site.name}</p>
                                <p className="text-[11px] text-muted-foreground">{site.category}</p>
                              </div>
                            </div>
                            {canChangeDirectly && (
                              <Button size="sm" variant="ghost" onClick={() => handleItemRequest('blacklist', 'remove', site.name)} className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive">
                                <Minus className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </TabsContent>

            {/* Content Restriction Tab */}
            <TabsContent value="content" className="mt-4 space-y-4">
              <p className="text-[13px] text-muted-foreground px-1">
                Control how images and videos are filtered based on content type.
              </p>

              {/* Image/Video Filtering Level */}
              <div className="bg-background rounded-2xl border border-border/50 shadow-sm overflow-hidden">
                <div className="px-4 py-4 border-b border-border/50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                      <Eye className="w-5 h-5 text-amber-600" />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-[15px] text-foreground">Image & Video Filtering</p>
                      <p className="text-[12px] text-muted-foreground">Block inappropriate images and video frames</p>
                    </div>
                  </div>
                </div>
                
                <div className="p-4 space-y-4">
                  <RadioGroup value={selectedFilterLevel} onValueChange={canChangeDirectly ? handleFilterLevelClick : undefined} className="space-y-3" disabled={!canChangeDirectly}>
                    {contentFilterLevels.map((level) => (
                      <label
                        key={level.id}
                        className={`flex items-start gap-3 p-4 rounded-xl border transition-all ${
                          selectedFilterLevel === level.id 
                            ? 'border-primary bg-primary/5' 
                            : 'border-border/50'
                        } ${canChangeDirectly ? 'cursor-pointer hover:bg-muted/30' : 'cursor-default'}`}
                      >
                        <RadioGroupItem value={level.id} className="mt-0.5" disabled={!canChangeDirectly} />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-semibold text-[14px] text-foreground">{level.title}</p>
                            {level.id === 'light' && (
                              <span className="px-2 py-0.5 rounded-full bg-green-500/10 text-green-600 text-[10px] font-medium">Minimal</span>
                            )}
                            {level.id === 'medium' && (
                              <span className="px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600 text-[10px] font-medium">Recommended</span>
                            )}
                            {level.id === 'strict' && (
                              <span className="px-2 py-0.5 rounded-full bg-red-500/10 text-red-600 text-[10px] font-medium">Maximum</span>
                            )}
                          </div>
                          <p className="text-[13px] text-muted-foreground mb-2">{level.description}</p>
                          <div className="flex flex-wrap gap-1.5">
                            {level.examples.map((example, idx) => (
                              <span 
                                key={idx} 
                                className="px-2 py-0.5 rounded-full bg-muted/50 text-muted-foreground text-[11px]"
                              >
                                {example}
                              </span>
                            ))}
                          </div>
                        </div>
                      </label>
                    ))}
                  </RadioGroup>
                </div>
              </div>

              {/* Block All Humans Option */}
              <div className="bg-background rounded-2xl border border-border/50 shadow-sm p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                      <User className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-[15px] text-foreground">Blur People in Images</p>
                      <p className="text-[13px] text-muted-foreground mt-1">
                        Automatically blur any detected humans in images.
                      </p>
                    </div>
                  </div>
                  <Switch 
                    checked={blockHumansEnabled} 
                    onCheckedChange={canChangeDirectly ? handleHumanBlockClick : undefined}
                    disabled={!canChangeDirectly}
                  />
                </div>
              </div>
            </TabsContent>
          </Tabs>
        )}

        {/* Whitelisted Browser Mode - Whitelist + Content tabs */}
        {currentModeId === 'whitelisted-browser' && (
          <Tabs defaultValue="sites" className="w-full">
            <TabsList className="w-full grid grid-cols-2 bg-muted/50 rounded-xl p-1 h-auto">
              <TabsTrigger 
                value="sites" 
                className="rounded-lg py-2.5 text-[14px] font-medium data-[state=active]:bg-background data-[state=active]:shadow-sm"
              >
                Allowed Sites
              </TabsTrigger>
              <TabsTrigger 
                value="content" 
                className="rounded-lg py-2.5 text-[14px] font-medium data-[state=active]:bg-background data-[state=active]:shadow-sm"
              >
                Content
              </TabsTrigger>
            </TabsList>

            <TabsContent value="sites" className="mt-4 space-y-3">
              <p className="text-[13px] text-muted-foreground px-1">
                Only sites you specifically approve can be visited. Add websites to allow access.
              </p>
              
              <div className="bg-background rounded-2xl border border-border/50 shadow-sm overflow-hidden">
                <div className="px-4 py-4 border-b border-border/50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                      <ListChecks className="w-5 h-5 text-primary" />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-[15px] text-foreground">Allowed Sites</p>
              <p className="text-[12px] text-muted-foreground">{allowlist.length} sites can be visited</p>
                    </div>
                  </div>
                </div>

                {/* Add site input */}
                <div className="px-4 py-3 border-b border-border/50">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newWhitelistSite}
                      onChange={(e) => setNewWhitelistSite(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddWhitelistSite()}
                      placeholder="Add a website (e.g. example.com)"
                      className="flex-1 h-9 rounded-lg border border-input bg-background px-3 text-[13px] placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    />
                    <button
                      onClick={handleAddWhitelistSite}
                      disabled={!newWhitelistSite.trim()}
                      className="h-9 w-9 rounded-lg bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary-hover active:scale-95 transition-all disabled:opacity-50"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="divide-y divide-border/50">
                  {allowlist.length === 0 ? (
                    <div className="px-4 py-6 text-center text-muted-foreground text-[13px]">
                      No allowed sites yet
                    </div>
                  ) : (
                    allowlist.map((site) => (
                      <div
                        key={site.name}
                        className="w-full px-4 py-3 flex items-center justify-between text-left"
                      >
                        <div className="flex items-center gap-3">
                          <Globe className="w-4 h-4 text-primary" />
                          <div>
                            <p className="text-[14px] font-medium text-foreground">{site.name}</p>
                            <p className="text-[11px] text-muted-foreground">{site.category}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleItemRequest('whitelist', 'remove', site.name)}
                          className="p-1.5 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-all"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Content Restriction Tab */}
            <TabsContent value="content" className="mt-4 space-y-4">
              <p className="text-[13px] text-muted-foreground px-1">
                Control how images and videos are filtered based on content type.
              </p>

              {/* Image/Video Filtering Level */}
              <div className="bg-background rounded-2xl border border-border/50 shadow-sm overflow-hidden">
                <div className="px-4 py-4 border-b border-border/50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                      <Eye className="w-5 h-5 text-amber-600" />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-[15px] text-foreground">Image & Video Filtering</p>
                      <p className="text-[12px] text-muted-foreground">Block inappropriate images and video frames</p>
                    </div>
                  </div>
                </div>
                
                <div className="p-4 space-y-4">
                  <RadioGroup value={selectedFilterLevel} onValueChange={canChangeDirectly ? handleFilterLevelClick : undefined} className="space-y-3" disabled={!canChangeDirectly}>
                    {contentFilterLevels.map((level) => (
                      <label
                        key={level.id}
                        className={`flex items-start gap-3 p-4 rounded-xl border transition-all ${
                          selectedFilterLevel === level.id 
                            ? 'border-primary bg-primary/5' 
                            : 'border-border/50'
                        } ${canChangeDirectly ? 'cursor-pointer hover:bg-muted/30' : 'cursor-default'}`}
                      >
                        <RadioGroupItem value={level.id} className="mt-0.5" disabled={!canChangeDirectly} />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-semibold text-[14px] text-foreground">{level.title}</p>
                            {level.id === 'light' && (
                              <span className="px-2 py-0.5 rounded-full bg-green-500/10 text-green-600 text-[10px] font-medium">Minimal</span>
                            )}
                            {level.id === 'medium' && (
                              <span className="px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600 text-[10px] font-medium">Recommended</span>
                            )}
                            {level.id === 'strict' && (
                              <span className="px-2 py-0.5 rounded-full bg-red-500/10 text-red-600 text-[10px] font-medium">Maximum</span>
                            )}
                          </div>
                          <p className="text-[13px] text-muted-foreground mb-2">{level.description}</p>
                          <div className="flex flex-wrap gap-1.5">
                            {level.examples.map((example, idx) => (
                              <span 
                                key={idx} 
                                className="px-2 py-0.5 rounded-full bg-muted/50 text-muted-foreground text-[11px]"
                              >
                                {example}
                              </span>
                            ))}
                          </div>
                        </div>
                      </label>
                    ))}
                  </RadioGroup>
                </div>
              </div>

              {/* Block All Humans Option */}
              <div className="bg-background rounded-2xl border border-border/50 shadow-sm p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                      <User className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-[15px] text-foreground">Blur People in Images</p>
                      <p className="text-[13px] text-muted-foreground mt-1">
                        Automatically blur any detected humans in images.
                      </p>
                    </div>
                  </div>
                  <Switch 
                    checked={blockHumansEnabled} 
                    onCheckedChange={canChangeDirectly ? handleHumanBlockClick : undefined}
                    disabled={!canChangeDirectly}
                  />
                </div>
              </div>
            </TabsContent>
          </Tabs>
        )}

        {/* No Browser Mode - Just show message */}
        {currentModeId === 'no-browser' && (
          <div className="bg-background rounded-2xl border border-border/50 shadow-sm p-6 text-center">
            <div className="w-16 h-16 rounded-2xl bg-destructive/10 flex items-center justify-center mx-auto mb-4">
              <Ban className="w-8 h-8 text-destructive" />
            </div>
            <h3 className="font-semibold text-[16px] text-foreground mb-2">Browser Disabled</h3>
            <p className="text-[13px] text-muted-foreground">
              Web browsing is completely disabled on this device. Use the "Request Change" button above to request a different browser mode.
            </p>
          </div>
        )}

      </div>

      {/* Item Change Confirmation Dialog */}
      <AlertDialog open={showItemConfirmation} onOpenChange={setShowItemConfirmation}>
        <AlertDialogContent className="max-w-[90vw] sm:max-w-md rounded-2xl bg-background">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {canChangeDirectly ? (
                <>
                  {requestAction?.type === 'category' && requestAction?.action === 'block' && `Block "${requestAction?.name}"?`}
                  {requestAction?.type === 'category' && requestAction?.action === 'allow' && `Allow "${requestAction?.name}"?`}
                  {requestAction?.type === 'whitelist' && requestAction?.action === 'add' && `Whitelist "${requestAction?.name}"?`}
                  {requestAction?.type === 'whitelist' && requestAction?.action === 'remove' && `Remove "${requestAction?.name}" from Whitelist?`}
                  {requestAction?.type === 'blacklist' && requestAction?.action === 'add' && `Blacklist "${requestAction?.name}"?`}
                  {requestAction?.type === 'blacklist' && requestAction?.action === 'remove' && `Remove "${requestAction?.name}" from Blacklist?`}
                </>
              ) : (
                <>
                  {requestAction?.type === 'category' && requestAction?.action === 'block' && `Request to Block "${requestAction?.name}"`}
                  {requestAction?.type === 'category' && requestAction?.action === 'allow' && `Request to Allow "${requestAction?.name}"`}
                  {requestAction?.type === 'whitelist' && requestAction?.action === 'add' && `Request to Whitelist "${requestAction?.name}"`}
                  {requestAction?.type === 'whitelist' && requestAction?.action === 'remove' && `Request to Remove "${requestAction?.name}" from Whitelist`}
                  {requestAction?.type === 'blacklist' && requestAction?.action === 'add' && `Request to Blacklist "${requestAction?.name}"`}
                  {requestAction?.type === 'blacklist' && requestAction?.action === 'remove' && `Request to Remove "${requestAction?.name}" from Blacklist`}
                </>
              )}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[14px]">
              {requestAction?.type === 'category' && requestAction?.action === 'block' && 
                `This will ${canChangeDirectly ? '' : 'request to '}block all websites in the "${requestAction?.name}" category.`}
              {requestAction?.type === 'category' && requestAction?.action === 'allow' && 
                `This will ${canChangeDirectly ? '' : 'request to '}allow access to websites in the "${requestAction?.name}" category.`}
              {requestAction?.type === 'whitelist' && requestAction?.action === 'add' && 
                `This will ${canChangeDirectly ? '' : 'request to '}always allow access to "${requestAction?.name}".`}
              {requestAction?.type === 'whitelist' && requestAction?.action === 'remove' && 
                `This will ${canChangeDirectly ? '' : 'request to '}remove "${requestAction?.name}" from the whitelist.`}
              {requestAction?.type === 'blacklist' && requestAction?.action === 'add' && 
                `This will ${canChangeDirectly ? '' : 'request to '}always block access to "${requestAction?.name}".`}
              {requestAction?.type === 'blacklist' && requestAction?.action === 'remove' && 
                `This will ${canChangeDirectly ? '' : 'request to '}remove "${requestAction?.name}" from the blacklist.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-0">
            <AlertDialogCancel className="rounded-xl" onClick={() => setRequestAction(null)} disabled={isSaving}>Cancel</AlertDialogCancel>
            <AlertDialogAction className="rounded-xl bg-primary text-primary-foreground" onClick={handleConfirmItemRequest} disabled={isSaving}>
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : canChangeDirectly ? 'Confirm' : 'Submit Request'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Filter Level Change Confirmation Dialog */}
      <AlertDialog open={showFilterConfirmation} onOpenChange={setShowFilterConfirmation}>
        <AlertDialogContent className="max-w-[90vw] sm:max-w-md rounded-2xl bg-background">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {canChangeDirectly ? 'Change Content Filter?' : 'Request Content Filter Change'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[14px] space-y-3">
              <p>{canChangeDirectly ? 'Change' : 'Would you like to request to change'} the content filtering level to <span className="font-semibold text-foreground">{getPendingFilterLevelInfo()?.title}</span>{canChangeDirectly ? '' : '?'}</p>
              {getPendingFilterLevelInfo() && (
                <div className="bg-muted/50 rounded-xl p-3 mt-2">
                  <p className="text-[13px] text-foreground mb-2">{getPendingFilterLevelInfo()?.description}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {getPendingFilterLevelInfo()?.examples.map((example, idx) => (
                      <span 
                        key={idx} 
                        className="px-2 py-0.5 rounded-full bg-background text-muted-foreground text-[11px]"
                      >
                        {example}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-0">
            <AlertDialogCancel className="rounded-xl" onClick={() => setPendingFilterLevel(null)} disabled={isSaving}>Cancel</AlertDialogCancel>
            <AlertDialogAction className="rounded-xl bg-primary text-primary-foreground" onClick={handleConfirmFilterChange} disabled={isSaving}>
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : canChangeDirectly ? 'Confirm' : 'Submit Request'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Human Block Change Confirmation Dialog */}
      <AlertDialog open={showHumanBlockConfirmation} onOpenChange={setShowHumanBlockConfirmation}>
        <AlertDialogContent className="max-w-[90vw] sm:max-w-md rounded-2xl bg-background">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {canChangeDirectly 
                ? (pendingHumanBlock ? 'Enable People Blur?' : 'Disable People Blur?')
                : (pendingHumanBlock ? 'Request to Enable People Blur' : 'Request to Disable People Blur')
              }
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[14px]">
              {pendingHumanBlock 
                ? `${canChangeDirectly ? 'This will' : 'Would you like to request to'} automatically blur any detected humans in images${canChangeDirectly ? '.' : '?'}`
                : `${canChangeDirectly ? 'This will' : 'Would you like to request to'} allow images with humans to be displayed without blur${canChangeDirectly ? '.' : '?'}`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-0">
            <AlertDialogCancel className="rounded-xl" onClick={() => setPendingHumanBlock(null)} disabled={isSaving}>Cancel</AlertDialogCancel>
            <AlertDialogAction className="rounded-xl bg-primary text-primary-foreground" onClick={handleConfirmHumanBlockChange} disabled={isSaving}>
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : canChangeDirectly ? 'Confirm' : 'Submit Request'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Browser;
